// Book a surgery: find the patient, say what is being done and to whom.
//
// A new case starts in DRAFT — clearly provisional until a theatre is booked.
// Theatre booking is deliberately NOT part of this form: booking is what turns
// the case from provisional to definite, and it belongs on the case where the
// conflict warnings can be shown against a case that already exists.
//
// OT never creates an admission (ADR-0001). The patient search surfaces any open
// admission so linking one is a click, but the link is optional and the case is
// perfectly valid without it — it just cannot be billed until one exists.

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Autocomplete, Box, Button, Typography } from "@mui/material";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { createCase, fetchSetup, searchPatients } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import StaffPicker from "../components/StaffPicker";
import { Field, FieldGrid, OtSelect, errorMessage, report } from "../components/common";
import { CASE_SOURCES, OT_BASE, SCHEDULING_TYPES } from "../constants";
import { applyStaffSelection } from "../utils/staffSelection";

const EMPTY = {
  case_source: "OP",
  scheduling_type: "ELECTIVE",
  admission_id: "",
  department_id: "",
  ward_name: "",
  // Read off the chosen patient's admission, for display only.
  // Never sent as case data: the server reads the live admission.
  admission_ward_name: "",
  admission_bed_label: "",
  anaesthesia_type_id: "",
  surgery_position_id: "",
  incision_type_id: "",
  estimated_duration_min: "",
  special_instructions: "",
  primary_surgeon_id: "",
  primary_surgeon_name: "",
  anaesthetist_id: "",
  anaesthetist_name: "",
  known_allergies: "",
  biohazard: false,
  biohazard_note: "",
  list_remarks: "",
  pac_required: true,
};

export default function OtCaseNew() {
  const history = useHistory();
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    fetchSetup()
      .then(setSetup)
      .catch((err) => setError(errorMessage(err, "Could not load the masters")))
      .finally(() => setLoading(false));
  }, []);

  // The search runs on the server; two characters is its own minimum, so there
  // is no point asking before then.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchPatients(query.trim())
        .then((rows) => {
          if (!cancelled) setResults(rows || []);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target?.value ?? e ?? "" }));

  const choosePatient = (value) => {
    setPatient(value);
    // An open admission is offered, never forced: a surgery booked today for a
    // patient who happens to be an inpatient for something else should not
    // silently attach itself to that admission.
    setForm((f) => ({
      ...f,
      admission_id: value?.open_admission_id || "",
      case_source: value?.open_admission_id ? "IPD" : f.case_source,
      // Kept so a case created for a patient with no admission still carries
      // whatever ward was typed; an admitted patient's is read, not stored.
      ward_name: value?.open_admission_id ? "" : f.ward_name,
      // Display only -- the server reads the live admission, it does not
      // trust these. They are here so the form can state where the patient
      // is the moment one is chosen.
      admission_ward_name: value?.ward_name || "",
      admission_bed_label: value?.bed_label || "",
    }));
  };

  const submit = async () => {
    if (!patient) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        patient_id: patient.patient_id,
        admission_id: form.admission_id || null,
      };
      const res = await createCase(payload);
      if (report(res, "OT case created")) {
        history.push(`${OT_BASE}/case/${res.data.id}`);
      }
    } catch (err) {
      report({ ok: false, message: errorMessage(err, "Could not create the case") });
    } finally {
      setSaving(false);
    }
  };

  const rooms = setup?.operating_rooms || [];
  const departments = setup?.departments || [];

  return (
    <OtScreen
      title="New Surgical Case Booking"
      description="Initiate new surgical case booking. Operating suite allocation confirms theatre schedule."
      loading={loading}
      error={error}
      actions={<Button onClick={() => history.push(`${OT_BASE}/cases`)}>Cancel</Button>}
    >
      <IpdSectionCard title="Patient Clinical Identification" sx={{ mb: 2.5 }}>
        <Autocomplete
          options={results}
          value={patient}
          onChange={(_e, value) => choosePatient(value)}
          onInputChange={(_e, value) => setQuery(value)}
          getOptionLabel={(o) =>
            o ? `${o.name || "Unnamed"} — ${o.uhid || o.patient_id}` : ""
          }
          isOptionEqualToValue={(a, b) => a.patient_id === b.patient_id}
          noOptionsText={
            query.trim().length < 2 ? "Enter at least 2 characters to search" : "No matching patient record found"
          }
          renderInput={(params) => (
            <IpdTextField {...params} fullWidth placeholder="Search by MRN, patient id or name" />
          )}
        />

        {patient && (
          <Box sx={{ mt: 2 }}>
            <FieldGrid columns={4}>
              <Field label="Patient Name" value={patient.name} />
              <Field label="Medical Record Number (MRN)" value={patient.uhid || patient.patient_id} />
              <Field label="Age / Sex" value={`${patient.age ?? "—"} / ${patient.gender || "—"}`} />
              <Field
                label="Linked Inpatient Admission"
                value={
                  patient.open_admission_id
                    ? `#${patient.open_admission_id} (linked)`
                    : "None — active inpatient admission required prior to theatre billing"
                }
              />
            </FieldGrid>
          </Box>
        )}
      </IpdSectionCard>

      <IpdSectionCard title="Scheduling Classification & Admission Linkage" sx={{ mb: 2.5 }}>
        <FieldGrid columns={3}>
          <IpdFormField label="Admission / Case Source">
            <OtSelect value={form.case_source} onChange={set("case_source")} options={CASE_SOURCES} />
          </IpdFormField>
          <IpdFormField label="Surgical Scheduling Classification">
            <OtSelect
              value={form.scheduling_type}
              onChange={set("scheduling_type")}
              options={SCHEDULING_TYPES}
            />
          </IpdFormField>
          <IpdFormField label="Inpatient Admission ID (optional)">
            <IpdTextField
              fullWidth
              value={form.admission_id}
              onChange={set("admission_id")}
              placeholder="IPD Admission Number (e.g., ADM-10293)"
            />
          </IpdFormField>
        </FieldGrid>
        <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          Daycare surgeries must specify their intake source (Outpatient, Inpatient, or Emergency referral) for bed allocation and billing workflows.
        </Typography>
      </IpdSectionCard>

      <IpdSectionCard title="Surgical Procedure & Clinical Parameters" sx={{ mb: 2.5 }}>
        <FieldGrid columns={3}>
          <IpdFormField label="Department">
            <OtSelect
              placeholder="Select Department..."
              value={form.department_id}
              onChange={set("department_id")}
              options={departments.map((d) => ({ value: d.id, label: d.name }))}
            />
          </IpdFormField>
          {/* An admitted patient's ward and bed are the ADMISSION's, and
              are stated rather than asked for. A ward typed here that
              disagreed with IPD was how a porter got sent to the wrong bed,
              with nothing anywhere comparing the two. A patient with no
              admission has nothing to read, so the field stays. */}
          <IpdFormField label="Ward">
            {form.admission_id ? (
              <Box sx={{ pt: 0.75 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {form.admission_ward_name || "Inpatient Ward Pending Allocation"}
                  {form.admission_bed_label
                    ? ` · Bed ${form.admission_bed_label}`
                    : " · Bed Unassigned"}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary }}>
                  Inherited from IPD Admission #{form.admission_id}
                </Typography>
              </Box>
            ) : (
              <IpdTextField fullWidth value={form.ward_name} onChange={set("ward_name")} />
            )}
          </IpdFormField>
          <IpdFormField label="Estimated Surgical Duration (Minutes)">
            <IpdTextField
              fullWidth
              type="number"
              value={form.estimated_duration_min}
              onChange={set("estimated_duration_min")}
            />
          </IpdFormField>

          <IpdFormField label="Anaesthesia Technique">
            <OtSelect
              placeholder="Select Anesthesia Technique..."
              value={form.anaesthesia_type_id}
              onChange={set("anaesthesia_type_id")}
              options={(setup?.anaesthesia_types || []).map((t) => ({ value: t.id, label: t.name }))}
            />
          </IpdFormField>
          <IpdFormField label="Patient Surgical Position">
            <OtSelect
              placeholder="Select Patient Position..."
              value={form.surgery_position_id}
              onChange={set("surgery_position_id")}
              options={(setup?.surgery_positions || []).map((t) => ({ value: t.id, label: t.name }))}
            />
          </IpdFormField>
          <IpdFormField label="Planned Incision">
            <OtSelect
              placeholder="Select Planned Incision..."
              value={form.incision_type_id}
              onChange={set("incision_type_id")}
              options={(setup?.incision_types || []).map((t) => ({ value: t.id, label: t.name }))}
            />
          </IpdFormField>

          <IpdFormField label="Consultant Operating Surgeon">
            <StaffPicker
              label="Select Primary Surgeon"
              valueName={form.primary_surgeon_name}
              onPick={(picked) =>
                setForm((f) =>
                  applyStaffSelection(f, "primary_surgeon_id", "primary_surgeon_name", picked)
                )
              }
            />
          </IpdFormField>
          <IpdFormField label="Consultant Anesthesiologist">
            <StaffPicker
              label="Select Anesthesiologist"
              valueName={form.anaesthetist_name}
              role="anaesthetist"
              onPick={(picked) =>
                setForm((f) =>
                  applyStaffSelection(f, "anaesthetist_id", "anaesthetist_name", picked)
                )
              }
            />
          </IpdFormField>
          <IpdFormField label="Pre-Anesthesia Clearance (PAC) Mandatory">
            <OtSelect
              value={form.pac_required ? "yes" : "no"}
              onChange={(e) =>
                setForm((f) => ({ ...f, pac_required: e.target.value === "yes" }))
              }
              options={[
                { value: "yes", label: "Yes — PAC required" },
                { value: "no", label: "No — Local/Minor procedure (Direct to Schedule)" },
              ]}
            />
          </IpdFormField>
        </FieldGrid>

        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Perioperative Clinical Directives & Special Equipment">
            <IpdTextField
              fullWidth
              multiline
              minRows={2}
              value={form.special_instructions}
              onChange={set("special_instructions")}
            />
          </IpdFormField>
        </Box>
      </IpdSectionCard>

      <IpdSectionCard
        title="Patient Safety Directives & Theatre Precautions"
        subtitle="Incorporate safety flags and clinical precautions onto the daily Operating List."
        sx={{ mb: 2.5 }}
      >
        <FieldGrid columns={3}>
          <IpdFormField label="Documented Drug / Latex Allergies">
            <IpdTextField
              fullWidth
              value={form.known_allergies}
              onChange={set("known_allergies")}
              placeholder="e.g. Penicillin, Latex"
            />
          </IpdFormField>
          <IpdFormField label="Bio-hazard / Enhanced Infection Control Precautions">
            <OtSelect
              value={form.biohazard ? "yes" : "no"}
              onChange={(e) => setForm((f) => ({ ...f, biohazard: e.target.value === "yes" }))}
              options={[
                { value: "no", label: "No" },
                { value: "yes", label: "Yes — Enhanced Barrier / Contact Precautions Required" },
              ]}
            />
          </IpdFormField>
          <IpdFormField label="Bio-hazard Clinical Specification">
            <IpdTextField
              fullWidth
              value={form.biohazard_note}
              onChange={set("biohazard_note")}
              disabled={!form.biohazard}
            />
          </IpdFormField>
        </FieldGrid>
        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Operating List Clinical Remarks">
            <IpdTextField fullWidth value={form.list_remarks} onChange={set("list_remarks")} />
          </IpdFormField>
        </Box>
      </IpdSectionCard>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button onClick={() => history.push(`${OT_BASE}/cases`)}>Cancel</Button>
        <IpdButton onClick={submit} disabled={!patient || saving}>
          {saving ? "Creating Case…" : "Create Surgical Case"}
        </IpdButton>
      </Box>
      {!patient && (
        <Typography sx={{ mt: 1, textAlign: "right", fontSize: 12, color: IPD_COLORS.textSecondary }}>
          Select an admitted or registered patient to proceed with OT booking. Active Operating Theatres: {rooms.length}
        </Typography>
      )}
    </OtScreen>
  );
}
